import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetVersionQuery } from "fond/api";
import { useFiberPathLength, useServiceLocationsCount } from "fond/hooks/useCostToServe";
import { BomOutput, Project, Store } from "fond/types";
import { isPlannerProject } from "fond/utils/project";

import { InteractiveBomView } from "../constants";
import { flattenBomData } from "../index";

import BomCostChart from "./BomCostChart";
import BomCostPerServiceLocationChart from "./BomCostPerServiceLocationChart";
import BomSummary from "./BomSummary";
import BomTable from "./BomTable";

interface BomContentProps {
  project: Project;
  bom: BomOutput;
}

const BomContent: React.FC<BomContentProps> = ({ project, bom }: BomContentProps) => {
  const versionId = useSelector((state: Store) => state.project.versionId);
  const { data: version } = useGetVersionQuery(versionId ?? skipToken);
  const [view, setView] = useState<InteractiveBomView>("analytics");

  const unitLabel = project.SystemOfMeasurement === "imperial" ? "ft" : "m";
  const rows = useMemo(() => flattenBomData(bom, project.SystemOfMeasurement), [bom, project.SystemOfMeasurement]);
  const lineItemCount = rows.length;
  const totalFiberPathLength = useFiberPathLength({ project, versionId });
  const serviceLocationsCount = useServiceLocationsCount({ project, versionId });

  const isGridView = view === "grid";

  return (
    <Box
      display="flex"
      flexDirection="column"
      height={isGridView ? "100%" : undefined}
      minHeight={isGridView ? 0 : "100%"}
      overflow={isGridView ? "hidden" : undefined}
      px="10%"
    >
      <BomSummary
        projectName={project.ProjectName}
        versionName={version?.Name ?? ""}
        lineItemCount={lineItemCount}
        totalCost={bom.total}
        totalPath={totalFiberPathLength}
        serviceLocationsCount={serviceLocationsCount}
        unitLabel={unitLabel}
        view={view}
        isPlannerProject={isPlannerProject(project)}
        onViewChange={setView}
      />
      {view === "analytics" && (
        <Grid container spacing={2.5} mb={2}>
          <BomCostChart rows={rows} totalCost={bom.total} />
          <BomCostPerServiceLocationChart project={project} versionId={versionId} />
        </Grid>
      )}
      <Box
        height={isGridView ? undefined : 700}
        flex={isGridView ? 1 : undefined}
        minHeight={isGridView ? 0 : undefined}
        flexShrink={0}
        display="flex"
        flexDirection="column"
      >
        <BomTable rows={rows} />
      </Box>
    </Box>
  );
};

export default BomContent;
